// drip-message-editor.tsx — the REAL message editor + REAL preview, reskinned
// for the Drip Campaigns redesign.
//
// This is NOT a re-implementation. The editing surface is the existing
// `BulkMessageEditorWrapper` → `MessageEditorControlled` (text + markdown
// toolbar + emoji + media/attachments + poll + link-preview + personalization
// tokens), exactly the component the legacy campaign Message step uses. The
// live preview is the real `WhatsAppMarkdownPreview` driven by the editor's
// own Redux draft.
//
// BRIDGE (per drip step, replicating bulk-set-message.tsx's machinery):
//   • The editor reads/writes ScheduledMessageEditorReducer; the wrapper
//     bridges that through CampaignReducer.currentTemplate (seed) and
//     currentTemplateDraft (write-back), but only syncs back when
//     activeTab === 'messageAndFiles'. So on open we seed currentTemplate from
//     step.template and force activeTab='messageAndFiles'; the wrapper's
//     debounced effect then keeps currentTemplateDraft in lock-step with the
//     editor. The parent reads currentTemplateDraft back into step.template.
//   • The wrapper keys its loadTemplateData effect on currentTemplate._id, so
//     each step carries a stable synthetic bridgeId — switching steps changes
//     the id and re-seeds the editor cleanly (one-step-at-a-time singleton).

import React from 'react';

import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { useIsMobile } from '@common/hooks/use-is-mobile';
import { setActiveTab } from '@common/reducers/CampaignReducer';
import { resetScheduledMessageEditor, loadTemplateData } from '@common/reducers/ScheduledMessageEditorReducer';
import { addScheduledMessageButtonActions } from '@common/reducers/AddScheduledMessageButtonReducer';
import PreviewMessageBubble from '../add-scheduled-message-button/preview-message-bubble';
import type { CommonRootState } from '@common/types/common-root-state-type';

import BulkMessageEditorWrapper from '../bt-bulk-dashboard/message-and-files/bulk-message-editor-wrapper';

import { Eyebrow } from './primitives';
import { templateToPreviewMessage, isEmptyPreview } from './campaign-message-bubble';

// A drip step template carries a synthetic, stable bridgeId in `_id` so the
// singleton editor's reseed fires when switching steps. The id is stripped at
// build time (drip steps don't persist as saved templates).
// Moved to @common-reducers/DripEditorReducer (the single source of truth for
// the drip working copy) so this module and wizard.tsx can both import from one
// place without a circular dependency; re-exported here so existing importers of
// this module keep working unchanged.
import {
  type DripStepTemplate,
  newStepTemplate,
  updateStepTemplate,
} from '@common-reducers/DripEditorReducer';
export type { DripStepTemplate };
export { newStepTemplate };

interface DripMessageEditorProps {
  /** Index of the step in DripEditorReducer.steps this editor is bound to. */
  idx: number;
  /** Render the inline preview column. The edit modals turn it off: they sit in
   *  EditorPreviewDialog, whose side panel (PreviewScheduleMessage) reads the
   *  same mirrored messageDraft — the schedule-message editor's layout. */
  showPreview?: boolean;
}

// The REAL editor + REAL preview for a single drip step. Mounting/unmounting
// this component IS the "open this step in the singleton editor" action.
export const DripMessageEditor: React.FC<DripMessageEditorProps> = ({ idx, showPreview = true }) => {
  // Mobile: stack the editor over the preview. The desktop side-by-side grid
  // crushes the editor beside the fixed 300px preview to ~24px at phone width.
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const { t } = useTranslation(namespace);

  // The step this editor edits is owned by DripEditorReducer (single source of
  // truth). Read it from the slice; if it's gone (e.g. removed mid-render),
  // render nothing.
  const step = useAppSelector((s: CommonRootState) => s.DripEditorReducer.steps[idx]);

  // Seed the singleton editor from this step, and switch the shared editor into
  // campaign ('messageAndFiles') mode. Re-runs when the step's bridgeId changes.
  // Seeds the ScheduledMessageEditorReducer DIRECTLY (no CampaignReducer bridge)
  // so there's no debounce and no reset-on-unmount that could drop the last edit.
  const seedId = step?.template._id;
  React.useEffect(() => {
    if (!step) return;
    dispatch(resetScheduledMessageEditor());
    dispatch(loadTemplateData(step.template));
    dispatch(setActiveTab('messageAndFiles'));
    // Cleanup: closing the step (collapse) or switching steps (bridgeId change)
    // tears down the singleton editor ONLY — the drip working copy lives in
    // DripEditorReducer and must survive the unmount (the mirror effect below
    // has already written every edit synchronously). React runs this before the
    // next seed and on final unmount.
    return () => {
      dispatch(resetScheduledMessageEditor());
    };
    // step read once per bridgeId — subsequent keystrokes flow editor → slice
    // via the mirror effect below, not back through here (no loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, seedId]);

  // MIRROR (no debounce): the live editor fields → the step in DripEditorReducer,
  // synchronously on every change. Replaces the old CampaignReducer draft bridge
  // (200ms debounce + reset-on-unmount) that lost the last edit on a fast unmount.
  const mMessage = useAppSelector((s: CommonRootState) => s.ScheduledMessageEditorReducer.message);
  const mIsPoll = useAppSelector((s: CommonRootState) => s.ScheduledMessageEditorReducer.isPoll);
  const mPoll = useAppSelector((s: CommonRootState) => s.ScheduledMessageEditorReducer.poll);
  const mAsset = useAppSelector((s: CommonRootState) => s.ScheduledMessageEditorReducer.asset);
  const mImageId = useAppSelector((s: CommonRootState) => s.ScheduledMessageEditorReducer.imageId);
  const mSendLinkPreview = useAppSelector((s: CommonRootState) => s.ScheduledMessageEditorReducer.sendLinkPreview);
  const mLinkPreview = useAppSelector((s: CommonRootState) => s.ScheduledMessageEditorReducer.linkPreview);
  const mTemplateName = useAppSelector((s: CommonRootState) => s.ScheduledMessageEditorReducer.templateName);

  React.useEffect(() => {
    if (!step) return;
    const cur = step.template;
    const mapped: DripStepTemplate = {
      _id: cur._id,
      message: mMessage,
      isPoll: mIsPoll,
      poll: mPoll,
      asset: mAsset,
      imageId: mImageId,
      sendLinkPreview: mSendLinkPreview,
      linkPreview: mLinkPreview,
      name: mTemplateName,
    };
    // Echo guard: the seed (loadTemplateData) populates the editor from the
    // step, which fires this effect once with values that already match the
    // step — skip that no-op write. Field-by-field shallow compare (references
    // for the object fields) also makes the effect converge after ≤2 writes
    // when loadTemplateData normalized a value (e.g. asset name/type null→'').
    const unchanged =
      cur.message === mapped.message &&
      cur.isPoll === mapped.isPoll &&
      cur.poll === mapped.poll &&
      cur.asset === mapped.asset &&
      cur.imageId === mapped.imageId &&
      cur.sendLinkPreview === mapped.sendLinkPreview &&
      cur.linkPreview === mapped.linkPreview &&
      cur.name === mapped.name;
    if (unchanged) return;
    dispatch(updateStepTemplate({ idx, template: mapped }));
  }, [
    dispatch,
    idx,
    step,
    mMessage,
    mIsPoll,
    mPoll,
    mAsset,
    mImageId,
    mSendLinkPreview,
    mLinkPreview,
    mTemplateName,
  ]);

  // Drive the REAL preview (PreviewMessageBubble) off the live editor state.
  // PreviewMessageBubble + its children (poll / media / link-preview / text)
  // read AddScheduledMessageButtonReducer.messageDraft when given no messageId
  // (see makeSelectMessageDraftById). The campaign bridge writes the live draft
  // into ScheduledMessageEditorReducer, NOT into that messageDraft — so we mirror
  // the live editor fields across here so the bubble renders polls as poll cards,
  // media as media, buttons as buttons, exactly like a real send. Scoped to this
  // editor's lifecycle and reset on unmount (see effect below) — no fork of the
  // preview, no leak into the real add-scheduled-message dialog.
  const editorMessage = useAppSelector((s: CommonRootState) => s.ScheduledMessageEditorReducer.message);
  const editorIsPoll = useAppSelector((s: CommonRootState) => s.ScheduledMessageEditorReducer.isPoll);
  const editorPoll = useAppSelector((s: CommonRootState) => s.ScheduledMessageEditorReducer.poll);
  const editorAsset = useAppSelector((s: CommonRootState) => s.ScheduledMessageEditorReducer.asset);
  const editorSendLinkPreview = useAppSelector((s: CommonRootState) => s.ScheduledMessageEditorReducer.sendLinkPreview);
  const editorLinkPreview = useAppSelector((s: CommonRootState) => s.ScheduledMessageEditorReducer.linkPreview);

  // A step with nothing in it drew a green WhatsApp bubble with NO CONTENT — an
  // empty rounded green rectangle, which reads as a rendering fault rather than
  // as "you haven't written anything yet". Every other Campaigns surface already
  // guards this (the campaigns list and the sequence detail both run
  // isEmptyPreview and show a muted note instead); the wizard's own message step,
  // the one place a person is actually about to write the message, was the only
  // surface that did not. Same helpers, so the four surfaces agree on what
  // "empty" means — a poll object of `{}` is empty, a link preview that does not
  // parse is empty, whitespace is empty.
  const previewIsEmpty = isEmptyPreview(templateToPreviewMessage({
    message: editorMessage,
    isPoll: editorIsPoll,
    poll: editorPoll,
    asset: editorAsset,
    linkPreview: editorSendLinkPreview ? editorLinkPreview : undefined,
  }));

  React.useEffect(() => {
    dispatch(addScheduledMessageButtonActions.updateMessageDraft({
      message: editorMessage,
      isPoll: editorIsPoll,
      poll: editorPoll,
      asset: editorAsset,
      sendLinkPreview: editorSendLinkPreview,
      linkPreview: editorLinkPreview,
    }));
  }, [dispatch, editorMessage, editorIsPoll, editorPoll, editorAsset, editorSendLinkPreview, editorLinkPreview]);

  // Reset the mirrored draft when this editor unmounts (collapse / step-switch)
  // so nothing leaks into the real add-scheduled-message dialog's preview.
  React.useEffect(() => {
    return () => {
      dispatch(addScheduledMessageButtonActions.updateMessageDraft({
        message: '',
        isPoll: false,
        poll: {},
        asset: undefined,
        sendLinkPreview: false,
        linkPreview: {},
      }));
    };
  }, [dispatch]);

  // Guard AFTER all hooks (Rules of Hooks): nothing to render if this step no
  // longer exists (e.g. removed mid-render).
  if (!step) return null;

  if (!showPreview) {
    return (
      <div className="campaigns-rd-editor">
        <BulkMessageEditorWrapper isPreview={false} />
      </div>
    );
  }

  // minmax(0, 1fr) (not plain 1fr): the real editor has an intrinsic
  // min-content width, so a plain 1fr track refuses to shrink below it and
  // pushes the fixed 300px preview column off the right edge of the pane.
  // minmax(0, …) lets the editor column shrink so the preview stays visible.
  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 300px', gap: 18 }}>
      {/* LEFT — the real editor (reskinned only by the surrounding .campaigns-rd wrapper) */}
      {/* LEFT — the real editor. Personalization is the editor's own control
          (the "Whatsapp name" button); the prototype's fabricated `first_name`
          hint was removed (it misrepresented the real token format). */}
      <div className="campaigns-rd-editor">
        <BulkMessageEditorWrapper isPreview={false} />
      </div>

      {/* RIGHT — the REAL WhatsApp preview (PreviewMessageBubble), driven by the
          live editor draft mirrored into messageDraft above. Renders polls as
          poll cards, media as media, buttons as buttons — exactly like a real send. */}
      <div>
        <Eyebrow style={{ marginBottom: 8 }}>{t('campaignsRedesign.wizard.previewEyebrow', 'Preview')}</Eyebrow>
        <div
          className="wa-wall"
          style={{
            borderRadius: 12, border: '1px solid var(--line)', padding: 14, minHeight: 160,
            display: 'flex',
            // The empty note is a state of the whole pane, so it sits in the
            // middle of it. A real bubble is centred across the pane too — one
            // message previewed on its own has no "sent" side to hug (matches
            // the sequence detail's step cards) — but stays at the top.
            alignItems: previewIsEmpty ? 'center' : 'flex-start',
            justifyContent: 'center',
          }}
        >
          {previewIsEmpty
            ? <span style={{ fontSize: 12.5, color: 'var(--muted)', fontStyle: 'italic', textAlign: 'center' }}>{t('campaignsRedesign.wizard.noMessageSet', 'Nothing written yet')}</span>
            : <PreviewMessageBubble shouldDisplayAckIcon={false} />}
        </div>
      </div>
    </div>
  );
};

// ════════ StepEditor — the ONE allowed editor bridge (§3/§4) ════════
// The single component that owns the seed→edit→write-back→reset protocol for
// ONE drip step at a time, so the wizard's single-message path, its multi-step
// cards and the edit modals all reuse exactly this surface (no ad-hoc bridging
// scattered around). Templates and Save as template are the editor toolbar's
// own buttons. Mounting it IS "open step i in the singleton editor"; unmounting
// it (collapse or step-switch) tears the singleton down via DripMessageEditor's
// cleanup.
interface StepEditorProps {
  /** Index of the step in DripEditorReducer.steps this editor is bound to. */
  idx: number;
  showPreview?: boolean;
}
export const StepEditor: React.FC<StepEditorProps> = ({ idx, showPreview }) => (
  <DripMessageEditor idx={idx} showPreview={showPreview} />
);
